/**
 * Sheet — right-slide panel for entity edit/view, deep-linkable, mobile fullscreen.
 *
 * Architectural Context: pure UI organism. Compound API: Sheet.Header, Sheet.Body, Sheet.Footer
 * supply structure; context exposes the close handler. The size token (sm/md/lg) maps to the
 * --surface-sheet-* CSS vars defined in index.css.
 *
 * @example
 * <Sheet open={open} onOpenChange={setOpen} size="md" dirty={isDirty}>
 *   <Sheet.Header>
 *     <Sheet.Title>Edit job</Sheet.Title>
 *     <Sheet.Description>Update job details</Sheet.Description>
 *   </Sheet.Header>
 *   <Sheet.Body>{form fields}</Sheet.Body>
 *   <Sheet.Footer>
 *     <Button type="text" onClick={close}>Cancel</Button>
 *     <Button type="contained" onClick={save}>Save</Button>
 *   </Sheet.Footer>
 * </Sheet>
 */
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/atoms/Button';

export type SheetSize = 'sm' | 'md' | 'lg';

export interface SheetProps {
  /** Controls visibility */
  open: boolean;
  /** Called when open state should change (close, ESC, overlay click) */
  onOpenChange: (open: boolean) => void;
  /** Size token: sm 480px / md 560px / lg 720px (mobile is always full-screen) */
  size?: SheetSize;
  /** When true, dismissal triggers a confirm("Discard unsaved changes?") prompt */
  dirty?: boolean;
  /** Override the dirty-check confirm prompt */
  dirtyConfirmMessage?: string;
  /** Close on overlay click. Default true. */
  closeOnOverlayClick?: boolean;
  children: ReactNode;
}

interface SheetContextValue {
  titleId: string;
  descriptionId: string | null;
  setHasDescription: (has: boolean) => void;
  onClose: () => void;
}

const SheetContext = createContext<SheetContextValue | null>(null);

const useSheetContext = () => {
  const ctx = useContext(SheetContext);
  if (!ctx) throw new Error('Sheet compound parts must be used inside <Sheet>');
  return ctx;
};

const SIZE_VAR: Record<SheetSize, string> = {
  sm: 'var(--surface-sheet-sm)',
  md: 'var(--surface-sheet-md)',
  lg: 'var(--surface-sheet-lg)',
};

/* ------------------------------------------------------------------ */
/*  Root                                                               */
/* ------------------------------------------------------------------ */

const SheetRoot = ({
  open,
  onOpenChange,
  size = 'md',
  dirty = false,
  dirtyConfirmMessage = 'You have unsaved changes. Discard them?',
  closeOnOverlayClick = true,
  children,
}: SheetProps) => {
  const titleId = useId();
  const generatedDescId = useId();
  const hasDescriptionRef = useRef(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const requestClose = useCallback(() => {
    if (dirty && typeof window !== 'undefined' && !window.confirm(dirtyConfirmMessage)) {
      return;
    }
    onOpenChange(false);
  }, [dirty, dirtyConfirmMessage, onOpenChange]);

  // Body scroll lock while open
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, requestClose]);

  // Focus the panel when opened
  useEffect(() => {
    if (open && panelRef.current) {
      panelRef.current.focus({ preventScroll: true });
    }
  }, [open]);

  const ctxValue = useMemo<SheetContextValue>(
    () => ({
      titleId,
      descriptionId: generatedDescId,
      setHasDescription: (has: boolean) => {
        hasDescriptionRef.current = has;
      },
      onClose: requestClose,
    }),
    [titleId, generatedDescId, requestClose],
  );

  if (!open) return null;

  return createPortal(
    <SheetContext.Provider value={ctxValue}>
      <div className="fixed inset-0 z-[120] flex" aria-hidden={false}>
        {/* Overlay */}
        <button
          type="button"
          aria-label="Close sheet"
          onClick={() => closeOnOverlayClick && requestClose()}
          className="absolute inset-0 bg-dark-invirtus/40 animate-fade-in cursor-default"
          tabIndex={-1}
        />
        {/* Panel — right-side drawer at the token width, full-screen on mobile */}
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={hasDescriptionRef.current ? generatedDescId : undefined}
          tabIndex={-1}
          className="absolute inset-y-0 right-0 w-full md:w-[var(--sheet-w)] flex flex-col bg-light shadow-[var(--shadow-sheet)] outline-none animate-sheet-slide-in"
          style={{ ['--sheet-w' as string]: SIZE_VAR[size], maxWidth: '100%' }}
        >
          {children}
        </div>
      </div>
    </SheetContext.Provider>,
    document.body,
  );
};

/* ------------------------------------------------------------------ */
/*  Header                                                             */
/* ------------------------------------------------------------------ */

interface SheetHeaderProps {
  children: ReactNode;
  /** Hide the close (×) button. Default false. */
  hideClose?: boolean;
}

const SheetHeader = ({ children, hideClose = false }: SheetHeaderProps) => {
  const { onClose } = useSheetContext();
  return (
    <div className="shrink-0 flex min-h-14 items-center justify-between gap-3 px-5 py-3 border-b border-light-lila/40 bg-light">
      <div className="flex-1 min-w-0">{children}</div>
      {!hideClose && (
        <Button
          type="icon"
          intent="muted"
          onClick={onClose}
          aria-label="Close"
          icon={<X />}
          className="shrink-0"
        />
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Title                                                              */
/* ------------------------------------------------------------------ */

const SheetTitle = ({ children, className = '' }: { children: ReactNode; className?: string }) => {
  const { titleId } = useSheetContext();
  return (
    <h5
      id={titleId}
      className={`text-base font-semibold text-dark-invirtus font-heading leading-snug ${className}`}
    >
      {children}
    </h5>
  );
};

/* ------------------------------------------------------------------ */
/*  Description                                                        */
/* ------------------------------------------------------------------ */

const SheetDescription = ({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) => {
  const { descriptionId, setHasDescription } = useSheetContext();
  useEffect(() => {
    setHasDescription(true);
    return () => setHasDescription(false);
  }, [setHasDescription]);
  return (
    <p id={descriptionId ?? undefined} className={`mt-0.5 text-sm text-dark-invirtus/60 font-body ${className}`}>
      {children}
    </p>
  );
};

/* ------------------------------------------------------------------ */
/*  Body                                                               */
/* ------------------------------------------------------------------ */

const SheetBody = ({ children, className = '' }: { children: ReactNode; className?: string }) => (
  <div
    className={`flex-1 min-h-0 min-w-0 overflow-y-auto [scrollbar-gutter:stable] px-5 py-4 ${className}`}
  >
    {children}
  </div>
);

/* ------------------------------------------------------------------ */
/*  Footer                                                             */
/* ------------------------------------------------------------------ */

const SheetFooter = ({ children, className = '' }: { children: ReactNode; className?: string }) => (
  <div
    className={`shrink-0 flex items-center justify-end gap-2 px-5 py-3 border-t border-light-lila/40 bg-light ${className}`}
  >
    {children}
  </div>
);

/* ------------------------------------------------------------------ */
/*  Compound export                                                    */
/* ------------------------------------------------------------------ */

export const Sheet = Object.assign(SheetRoot, {
  Header: SheetHeader,
  Title: SheetTitle,
  Description: SheetDescription,
  Body: SheetBody,
  Footer: SheetFooter,
});
